// src/pages/DataExplorer.jsx - FULLY INTEGRATED
import React, { useState, useEffect } from 'react';
import {
  Database,
  Search,
  Filter,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  GitBranch,
  GitCommit,
  GitPullRequest,
  FileText,
  Users,
  MessageSquare,
  Calendar,
  Tag,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import {
  TableWrapper,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table.jsx';
import { apiCall } from '../utils/api';
import { canCreateConnector } from '../utils/roles';

// Entity icon mapping
const ENTITY_ICONS = {
  repos: GitBranch,
  repositories: GitBranch,
  commits: GitCommit,
  pulls: GitPullRequest,
  pull_requests: GitPullRequest,
  issues: FileText,
  teams: Users,
  users: Users,
  comments: MessageSquare,
  issue_comments: MessageSquare,
  pull_reviews: MessageSquare,
  messages: MessageSquare,
  channels: MessageSquare,
  releases: Tag,
  milestones: Calendar,
  calendar: Calendar,
  sprints: Calendar,
  projects: Database,
  boards: Database,
  files: FileText,
  reactions: Tag,
};

export default function DataExplorer() {
  // State
  const [availableConnectors, setAvailableConnectors] = useState([]);
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [availableEntities, setAvailableEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [records, setRecords] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const itemsPerPage = 10;

  // Load connectors on mount
  useEffect(() => {
    loadConnectors();
  }, []);

  // Load entities when connector changes
  useEffect(() => {
    if (selectedConnector) {
      loadEntities();
    }
  }, [selectedConnector]);

  // Load data when connector, entity, page, or search changes
  useEffect(() => {
    if (selectedConnector && selectedEntity) {
      loadData();
    }
  }, [selectedConnector, selectedEntity, currentPage, searchQuery]);

  async function loadConnectors() {
    setLoading(true);
    setError(null);
    
    try {
      const res = await apiCall('/sync/configs');
      if (res.ok) {
        const data = await res.json();
        setAvailableConnectors(data.configs || []);
        
        // Auto-select first connector
        if (data.configs && data.configs.length > 0) {
          setSelectedConnector(data.configs[0].id);
        }
      } else {
        throw new Error('Failed to load connectors');
      }
    } catch (err) {
      console.error('Failed to load connectors:', err);
      setError('Failed to load connectors. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function loadEntities() {
    try {
      // Get entities with counts
      const res = await apiCall(`/data/entities?connector_config_id=${selectedConnector}`);
      if (res.ok) {
        const data = await res.json();
        
        // Format entities for UI
        const entities = (data.entities || []).map(e => ({
          id: e.entity,
          label: formatEntityLabel(e.entity),
          icon: ENTITY_ICONS[e.entity] || Database,
          count: e.count,
          lastSynced: e.last_synced
        }));
        
        setAvailableEntities(entities);
        
        // Auto-select first entity
        if (entities.length > 0) {
          setSelectedEntity(entities[0].id);
          setCurrentPage(1); // Reset to page 1
        } else {
          setSelectedEntity(null);
        }
      } else {
        // Fallback: use /sync/configs/{id}/entities if /data/entities fails
        const fallbackRes = await apiCall(`/sync/configs/${selectedConnector}/entities`);
        if (fallbackRes.ok) {
          const fallbackData = await fallbackRes.json();
          const entities = (fallbackData.entities || []).map(e => ({
            id: e,
            label: formatEntityLabel(e),
            icon: ENTITY_ICONS[e] || Database,
            count: null, // No count available
            lastSynced: null
          }));
          
          setAvailableEntities(entities);
          
          if (entities.length > 0) {
            setSelectedEntity(entities[0].id);
            setCurrentPage(1);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load entities:', err);
      setAvailableEntities([]);
    }
  }

  async function loadData() {
    setLoadingData(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        connector_config_id: selectedConnector,
        entity: selectedEntity,
        page: currentPage,
        limit: itemsPerPage
      });
      
      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }
      
      const res = await apiCall(`/data/browse?${params}`);
      
      if (res.ok) {
        const data = await res.json();
        setRecords(data.records || []);
        setTotalRecords(data.total || 0);
        setTotalPages(data.total_pages || 0);
      } else {
        throw new Error('Failed to load data');
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load data. Please try again.');
      setRecords([]);
    } finally {
      setLoadingData(false);
    }
  }

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadData();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1); // Reset to page 1 on search
  };

  if (loading) {
    return (
      <div className="h-[calc(100vh-4rem)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (availableConnectors.length === 0) {
    return (
      <div className="h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="text-center">
          <Database className="w-12 h-12 text-[#6a6a6a] mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-white mb-2">No Connectors Found</h2>
          <p className="text-sm text-[#a0a0a0] mb-4">
            Create a connector first to browse data
          </p>
          {canCreateConnector() && (
            <Button onClick={() => window.location.href = '/connectors/new'}>
              Create Connector
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col">
      {/* Top Toolbar */}
      <div className="pb-6">
        <Toolbar
          availableConnectors={availableConnectors}
          selectedConnector={selectedConnector}
          onConnectorChange={(id) => {
            setSelectedConnector(id);
            setCurrentPage(1);
          }}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
        />
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6 min-h-0">
        {/* Left Panel - Entity Navigation */}
        <div className="overflow-y-auto scrollbar-thin">
          <EntityNavigation
            entities={availableEntities}
            selected={selectedEntity}
            onSelect={(entity) => {
              setSelectedEntity(entity);
              setCurrentPage(1);
            }}
          />
        </div>

        {/* Right Panel - Data Table */}
        <div className="overflow-y-auto scrollbar-thin">
          <DataTablePanel
            entity={selectedEntity}
            records={records}
            loading={loadingData}
            searchQuery={searchQuery}
            currentPage={currentPage}
            totalPages={totalPages}
            totalRecords={totalRecords}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>
    </div>
  );
}

// ==========================================
// TOOLBAR
// ==========================================
function Toolbar({ 
  availableConnectors, 
  selectedConnector, 
  onConnectorChange, 
  searchQuery, 
  onSearchChange, 
  onRefresh, 
  isRefreshing 
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      {/* Left - Title */}
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <div className="min-w-0">
          <h1 className="text-3xl font-semibold tracking-tight text-white truncate">
            Data Explorer
          </h1>
          <p className="text-sm text-[#a0a0a0] mt-1">
            Browse and explore your connector data
          </p>
        </div>
      </div>

      {/* Right - Actions */}
      <div className="flex items-center gap-3 flex-shrink-0">
        {/* Connector Selector */}
        <div className="w-48">
          <select
            value={selectedConnector || ''}
            onChange={(e) => onConnectorChange(parseInt(e.target.value))}
            className="w-full h-9 px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] text-white text-sm rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-colors"
          >
            {availableConnectors.map(conn => (
              <option key={conn.id} value={conn.id}>
                {conn.display_name} (ID: {conn.id})
              </option>
            ))}
          </select>
        </div>

        {/* Search */}
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6a6a6a]" />
          <Input
            type="text"
            placeholder="Search records..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Refresh Button */}
        <Button
          variant="secondary"
          size="icon"
          onClick={onRefresh}
          disabled={isRefreshing}
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
        </Button>
      </div>
    </div>
  );
}

// ==========================================
// ENTITY NAVIGATION
// ==========================================
function EntityNavigation({ entities, selected, onSelect }) {
  if (entities.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <Database className="w-8 h-8 text-[#6a6a6a] mx-auto mb-2" />
          <p className="text-sm text-[#6a6a6a]">No entities found</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-3">
        <div className="space-y-1">
          {entities.map((entity) => {
            const Icon = entity.icon;
            const isSelected = selected === entity.id;

            return (
              <button
                key={entity.id}
                onClick={() => onSelect(entity.id)}
                className={`
                  w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                  ${isSelected
                    ? 'bg-blue-500/10 text-blue-400'
                    : 'text-[#a0a0a0] hover:text-white hover:bg-[#222222]'
                  }
                `}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{entity.label}</span>
                </div>
                {entity.count !== null && (
                  <Badge variant="neutral" className="flex-shrink-0">
                    {formatCount(entity.count)}
                  </Badge>
                )}
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ==========================================
// DATA TABLE PANEL
// ==========================================
function DataTablePanel({ 
  entity, 
  records, 
  loading,
  searchQuery, 
  currentPage, 
  totalPages,
  totalRecords,
  itemsPerPage,
  onPageChange 
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search Summary */}
      {searchQuery && (
        <div className="flex items-center gap-2">
          <Badge variant="neutral">
            {totalRecords} result{totalRecords !== 1 ? 's' : ''} for "{searchQuery}"
          </Badge>
        </div>
      )}

      {/* Data Table */}
      <TableWrapper>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {records.length > 0 ? (
              records.map((record) => (
                <TableRow key={record.id}>
                  <TableCell>
                    <DataRecordDisplay data={record.data} entity={entity} />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell className="text-center py-12">
                  <div className="flex flex-col items-center gap-2">
                    <Database className="w-8 h-8 text-[#6a6a6a]" />
                    <p className="text-sm font-medium text-white">No data found</p>
                    <p className="text-sm text-[#6a6a6a]">
                      {searchQuery ? 'Try adjusting your search' : 'No records synced yet'}
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>

      {/* Pagination */}
      {totalRecords > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalRecords}
          itemsPerPage={itemsPerPage}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}

// ==========================================
// DATA RECORD DISPLAY
// ==========================================
function DataRecordDisplay({ data, entity }) {
  // Display record data in a readable format
  return (
    <div className="space-y-2">
      {Object.entries(data).map(([key, value]) => {
        // Skip internal fields
        if (key === '_id' || key === '__v') return null;
        
        return (
          <div key={key} className="flex gap-2">
            <span className="text-sm font-medium text-[#a0a0a0] min-w-[120px]">
              {formatFieldName(key)}:
            </span>
            <span className="text-sm text-white">
              {formatFieldValue(value)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ==========================================
// PAGINATION
// ==========================================
function Pagination({ currentPage, totalPages, totalItems, itemsPerPage, onPageChange }) {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="flex items-center justify-between">
      <p className="text-sm text-[#a0a0a0]">
        Showing {startItem} to {endItem} of {totalItems} results
      </p>

      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="icon"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>

        <div className="flex items-center gap-1">
          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }

            return (
              <button
                key={pageNum}
                onClick={() => onPageChange(pageNum)}
                className={`
                  w-9 h-9 rounded-lg text-sm font-medium transition-colors
                  ${currentPage === pageNum
                    ? 'bg-blue-500 text-white'
                    : 'text-[#a0a0a0] hover:text-white hover:bg-[#1a1a1a]'
                  }
                `}
              >
                {pageNum}
              </button>
            );
          })}
        </div>

        <Button
          variant="secondary"
          size="icon"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

// ==========================================
// HELPER FUNCTIONS
// ==========================================
function formatEntityLabel(entity) {
  return entity
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
}

function formatFieldName(key) {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
}

function formatFieldValue(value) {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  if (typeof value === 'string' && value.length > 100) {
    return value.substring(0, 100) + '...';
  }
  return String(value);
}

function formatCount(count) {
  if (count >= 1000000) return (count / 1000000).toFixed(1) + 'M';
  if (count >= 1000) return (count / 1000).toFixed(1) + 'K';
  return count.toString();
}
